import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import MarketplaceViewport from './marketplace/MarketplaceViewport';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navigation from './Navigation';
import UnifiedBuyRequestResponseModal from './dashboard/UnifiedBuyRequestResponseModal';
import ListingDetailModal from './ListingDetailModal';
import EngineListingDetailModal from './engines/EngineListingDetailModal';
import PartsListingDetailModal from './parts/PartsListingDetailModal';
import JobListingDetailModal from './jobs/JobListingDetailModal';
import ServiceDetailModal from './services/ServiceDetailModal';
import { Pagination } from './Pagination';
import EmptyState from './EmptyState';
import { Skeleton } from './ui/skeleton';
import { Button } from './ui/button';
import { extractListingImages } from '@/utils/listingDataTransform';
import { UniversalMap, MapMarker } from '@/components/maps';
import { MarketplaceSearchInput } from './marketplace/MarketplaceSearchInput';
import { MarketplaceListingCard } from './marketplace/MarketplaceListingCard';
import { normalizeListingForCard } from '@/utils/marketplaceCardNormalizer';
import { useMarketplaceListings, MarketplaceFilters } from '@/hooks/useMarketplaceListings';
import { Grid3X3, List, Map as MapIcon, SlidersHorizontal, Package, Image, Disc, Briefcase, Settings } from 'lucide-react';
import HelicopterSilhouetteIcon from "@/components/icons/HelicopterSilhouetteIcon";
import { JOB_CATEGORY_OPTIONS } from '@/types/jobWizard';
import { SERVICE_CATEGORY_CONFIG, ServiceCategory, getServiceCategoryLabel } from '@/utils/serviceTypeIcons';
import { EngineAssetIcon } from '@/components/EngineAssetIcon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { useOptimizedConversations as useConversations } from '@/hooks/useOptimizedConversations';
import { useAuth } from './AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { FilterHierarchy } from './marketplace/FilterHierarchy';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from './ui/resizable';
import { ScrollArea } from './ui/scroll-area';
import { MobileFilterDrawer } from './marketplace/MobileFilterDrawer';

import { MobileListView } from './marketplace/MobileListView';
import { MobileMapPreviewCard } from './marketplace/MobileMapPreviewCard';
import MobilePageHeader from '@/components/mobile/MobilePageHeader';
import { useIsMobile } from '@/hooks/use-mobile';
import { useIsTablet } from '@/hooks/use-tablet';
import { cn } from '@/lib/utils';
import { useSharedUserPreferences } from '@/contexts/UserPreferencesContext';
import { useLocationGrantsMap } from '@/hooks/useLocationGrantsMap';
import { CategoryDivider, getListingTypeSortOrder } from './marketplace/CategoryDivider';
// SEO metadata generator
const getMarketplaceSEO = (assetType: string, listingCount: number) => {
  const assetLabels = {
    helicopter: 'Helicopters',
    engine: 'Turbine Engines', 
    parts: 'Parts & Tools',
    jobs: 'Aviation Jobs',
    services: 'Aviation Services'
  };
  const label = assetLabels[assetType as keyof typeof assetLabels] || 'Helicopters';
  return {
    title: `${label} Marketplace - Browse ${listingCount > 0 ? listingCount : ''} Verified Listings | ChopperSwap`,
    description: `Browse verified ${label.toLowerCase()} listings on ChopperSwap. Filter by manufacturer, price, location and more. Find your perfect aircraft today.`
  };
};

const EnhancedMarketplace: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { createOrGetConversation } = useConversations();
  const { preferences } = useSharedUserPreferences();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Asset type toggle: helicopter, engine, parts, jobs, or services
  const assetType = (searchParams.get('asset') as 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services') || 'helicopter';
  
  // Split search into input state (immediate) and debounced state (drives queries)
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchParams.get('search') || '');
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const isTypingRef = useRef(false);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // Single batched URL→state sync effect (replaces 5 separate effects)
  // Reads all URL params in one pass to avoid cascading re-renders
  useEffect(() => {
    if (isTypingRef.current) return;
    const urlSearch = searchParams.get('search') || '';
    if (urlSearch !== searchInput) {
      setSearchInput(urlSearch);
      setDebouncedSearchQuery(urlSearch);
    }
    const urlView = (searchParams.get('view') as 'grid' | 'list' | 'map') || 'map';
    if (urlView !== viewMode) setViewMode(urlView);
    const urlSort = searchParams.get('sort') || 'date-desc';
    if (urlSort !== sortBy) setSortBy(urlSort);
    const urlMarket = (searchParams.get('market') as 'all' | 'sell' | 'buy') || 'all';
    if (urlMarket !== marketMode) setMarketMode(urlMarket);
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    if (urlPage !== currentPage) setCurrentPage(urlPage);
  }, [searchParams]);
  const initialViewMode = (searchParams.get('view') as 'grid' | 'list' | 'map') || 'map';
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>(initialViewMode);
  const [markerDisplayMode, setMarkerDisplayMode] = useState<'icon' | 'hero'>('icon');
  const [showFilters, setShowFilters] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [selectedListing, setSelectedListing] = useState<any>(null);
  const [selectedMapListingId, setSelectedMapListingId] = useState<string | null>(null);
  const [selectedPartsListing, setSelectedPartsListing] = useState<any>(null);
  const [partsDetailOpen, setPartsDetailOpen] = useState(false);
  const [selectedJobListing, setSelectedJobListing] = useState<any>(null);
  const [jobDetailOpen, setJobDetailOpen] = useState(false);
  const [selectedServiceListing, setSelectedServiceListing] = useState<any>(null);
  const [serviceDetailOpen, setServiceDetailOpen] = useState(false);
  
  // Mobile map toggle state
  const [mobileMapVisible, setMobileMapVisible] = useState(false);
  
  // Synchronized loading state - content fades in when both listings AND map are ready
  const [contentReady, setContentReady] = useState(false);
  // Track whether we've ever successfully rendered data (prevents full-page remount on refetch)
  const hasEverLoadedRef = useRef(false);
  // Track whether modal was opened locally (vs deep-link hydration) to gate scroll side effects
  const localModalOpenRef = useRef(false);
  
  
  // New state for sell/buy distinction (helicopter and parts)
  const [sellListingType, setSellListingType] = useState<string | null>(
    searchParams.get('mode') === 'sell' ? searchParams.get('type') : null
  );
  const [buyRequestType, setBuyRequestType] = useState<string | null>(
    searchParams.get('mode') === 'buy' ? searchParams.get('type') : null
  );
  
  // Parts listing type state
  const [partsListingType, setPartsListingType] = useState<string | null>(
    searchParams.get('parts_type') || null
  );
  
  // Market mode: 'all' | 'sell' | 'buy' - filters between sale listings and buy requests
  const [marketMode, setMarketMode] = useState<'all' | 'sell' | 'buy'>(
    (searchParams.get('market') as 'all' | 'sell' | 'buy') || 'all'
  );
  
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'date-desc');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const itemsPerPage = 20;
  const [selectedListingId, setSelectedListingId] = useState<string | null>(searchParams.get('listing') || searchParams.get('engine') || searchParams.get('parts'));
  const [selectedRequestForListing, setSelectedRequestForListing] = useState<any>(null);
  const [responseAssetType, setResponseAssetType] = useState<'helicopter' | 'engine' | 'parts' | 'jobs' | 'services'>('helicopter');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Shared asset type toggle renderer to avoid duplicating ~180 lines across 3 locations
  const renderAssetToggle = useCallback((variant: 'desktop' | 'collapsed' | 'mobile') => {
    const assets = [
      { key: 'helicopter', label: 'Helicopters', ariaLabel: 'Helicopters',
        icon: (active: boolean) => <HelicopterSilhouetteIcon tone={active ? 'inverse' : 'nav'} active={active} className="h-4 w-4" /> },
      { key: 'engine', label: 'Engines', ariaLabel: 'Engines',
        icon: (active: boolean) => <EngineAssetIcon className="h-4 w-4" inverted={active} /> },
      { key: 'parts', label: 'Parts', ariaLabel: 'Parts',
        icon: () => <Package className="h-4 w-4" /> },
      { key: 'jobs', label: 'Jobs', ariaLabel: 'Jobs',
        icon: () => <Briefcase className="h-4 w-4" /> },
      { key: 'services', label: 'Services', ariaLabel: 'Services',
        icon: () => <Settings className="h-4 w-4" /> },
    ];

    return assets.map(({ key, label, ariaLabel, icon }) => {
      const active = assetType === key;
      return (
        <Button
          key={key}
          variant={active ? 'default' : 'ghost'}
          size={variant === 'mobile' ? 'icon' : 'sm'}
          aria-label={variant === 'mobile' ? ariaLabel : undefined}
          onClick={() => {
            const params = new URLSearchParams(searchParams);
            params.set('asset', key);
            setSearchParams(params);
          }}
          className={cn(
            variant === 'desktop' && "gap-2 rounded-xl transition-all duration-300",
            variant === 'desktop' && active && "shadow-lg shadow-primary/30 ring-2 ring-primary/20",
            variant === 'collapsed' && "gap-2",
            variant === 'mobile' && "h-8 w-8 rounded-lg transition-all duration-300",
            variant === 'mobile' && active && "shadow-md shadow-primary/25"
          )}
        >
          {icon(active)}
          {variant !== 'mobile' && label}
        </Button>
      );
    });
  }, [assetType, searchParams, setSearchParams]);

  // Shared market mode toggle renderer to avoid duplicating ~120 lines across 3 locations
  const renderMarketModeToggle = useCallback((variant: 'desktop' | 'collapsed' | 'mobile') => {
    const sellLabel = assetType === 'services' ? 'Providers' : assetType === 'jobs' ? 'Companies' : 'Sale';
    const buyLabel = assetType === 'services' ? 'Seekers' : assetType === 'jobs' ? (variant === 'mobile' ? 'Pros' : 'Professionals') : (variant === 'mobile' ? 'Want' : 'Buy');

    const modes = [
      { key: 'all' as const, label: 'All' },
      { key: 'sell' as const, label: sellLabel },
      { key: 'buy' as const, label: buyLabel },
    ];

    return modes.map(({ key, label }) => (
      <Button
        key={key}
        variant={marketMode === key ? 'default' : 'ghost'}
        size="sm"
        className={cn(
          variant === 'mobile' && cn(
            "h-7 px-2.5 text-[10px] rounded-lg transition-all duration-300",
            marketMode === key && "shadow-sm",
            marketMode === key && key !== 'all' && (assetType === 'jobs' || assetType === 'services') && (
              key === 'sell' ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-500 hover:bg-amber-600"
            )
          ),
          variant === 'collapsed' && "",
          variant === 'desktop' && cn(
            "rounded-lg transition-all duration-300",
            marketMode === 'all' && key === 'all' && "bg-slate-700 dark:bg-slate-600 shadow-md",
            marketMode === 'sell' && key === 'sell' && "bg-gradient-to-r from-emerald-500 to-emerald-600 shadow-lg shadow-emerald-500/30 ring-2 ring-emerald-300/40 hover:from-emerald-600 hover:to-emerald-700",
            marketMode === 'buy' && key === 'buy' && "bg-gradient-to-r from-amber-500 to-amber-600 shadow-lg shadow-amber-500/30 ring-2 ring-amber-300/40 hover:from-amber-600 hover:to-amber-700"
          )
        )}
        onClick={() => {
          setMarketMode(key);
          const params = new URLSearchParams(searchParams);
          if (key === 'all') params.delete('market');
          else params.set('market', key);
          setSearchParams(params, { replace: true });
        }}
      >
        {label}
      </Button>
    ));
  }, [assetType, marketMode, searchParams, setSearchParams]);

  // Shared view mode buttons renderer to avoid duplicating ~60 lines
  const renderViewModeButtons = useCallback(() => {
    const allModes = [
      { key: 'grid' as const, icon: <Grid3X3 className="h-4 w-4" />, label: 'grid view' },
      { key: 'list' as const, icon: <List className="h-4 w-4" />, label: 'list view' },
      { key: 'map' as const, icon: <MapIcon className="h-4 w-4" />, label: 'map view' },
    ];
    // On mobile, grid and list render the same layout — only show map toggle
    const modes = isMobile ? allModes.filter(m => m.key === 'map') : allModes;
    return (
      <>
        {modes.map(({ key, icon, label }) => (
          <Button
            key={key}
            variant={viewMode === key ? 'default' : 'ghost'}
            size="icon"
            aria-label={label}
            title={label}
            onClick={() => {
              setViewMode(key);
              const params = new URLSearchParams(searchParams);
              params.set('view', key);
              setSearchParams(params, { replace: true });
            }}
          >
            {icon}
          </Button>
        ))}
        {viewMode === 'map' && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMarkerDisplayMode(prev => prev === 'icon' ? 'hero' : 'icon')}
            title={markerDisplayMode === 'icon' ? 'Show Photos' : 'Show Icons'}
            aria-label={markerDisplayMode === 'icon' ? 'Show photos' : 'Show icons'}
            className="ml-1"
          >
            {markerDisplayMode === 'icon' ? (
              <Image className="h-4 w-4" />
            ) : (
              <Disc className="h-4 w-4" />
            )}
          </Button>
        )}
      </>
    );
  }, [viewMode, markerDisplayMode, searchParams, setSearchParams]);


  // Map viewport bounding box for server-side filtering (Phase 2 optimization)
  const [mapBounds, setMapBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null);
  
  // Track if user has manually interacted with map (panned/zoomed) to avoid filtering on initial fitBounds
  const [userHasInteractedWithMap, setUserHasInteractedWithMap] = useState(false);
  // Track initial bounds update count to skip first 2 (initial load + fitBounds auto-center)
  const boundsUpdateCountRef = React.useRef(0);
  
  // Filters - flexible to support helicopters, engines, and parts
  // All range filters default to null = no filtering until user explicitly sets them
  const [filters, setFilters] = useState<MarketplaceFilters>({
    priceRange: null, // null = no filtering
    seatingRange: null, // null = no filtering (helicopter only)
    tsnRange: null, // null = no filtering (engine only)
    manufacturers: [],
    models: [],
    listingType: 'all',
    condition: 'all',
    yearRange: null, // null = no filtering (helicopter only)
    location: ''
  });

  // === Server-side paginated listings hook ===
  const paginatedQuery = useMarketplaceListings({
    assetType,
    filters,
    searchQuery: debouncedSearchQuery,
    sellListingType,
    buyRequestType,
    partsListingType,
    marketMode,
    sortBy,
    enabled: true,
    // Map viewport filtering disabled for stability - automatic viewport filtering
    // caused listing counts to drop unexpectedly. Can be re-enabled with explicit 
    // "Search this area" button in future phase.
    boundingBox: undefined,
  });

  const effectiveListings = paginatedQuery.allListings;
  const effectiveLoading = paginatedQuery.isLoading;
  const effectiveManufacturers = paginatedQuery.manufacturers;

  // (duplicate search sync removed — single canonical sync is above)

  // (viewMode, sortBy, marketMode, currentPage URL syncs consolidated into single batched effect above)

  // Handler to update search — input updates instantly, query + URL debounced
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    isTypingRef.current = true;

    // Immediately clear listing selection when user starts typing
    // The results are about to change, so the old selection is stale
    if (selectedListing || selectedPartsListing || selectedJobListing || selectedServiceListing || selectedMapListingId) {
      setSelectedListing(null);
      setSelectedListingId(null);
      setSelectedMapListingId(null);
      setSelectedPartsListing(null);
      setPartsDetailOpen(false);
      setSelectedJobListing(null);
      setJobDetailOpen(false);
      setSelectedServiceListing(null);
      setServiceDetailOpen(false);
    }

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      isTypingRef.current = false;
      setDebouncedSearchQuery(value);
      const params = new URLSearchParams(window.location.search);
      if (value) {
        params.set('search', value);
      } else {
        params.delete('search');
      }
      // Clear all listing selection params — search invalidates previous selection
      params.delete('listing');
      params.delete('engine');
      params.delete('parts');
      params.delete('job');
      params.delete('service');
      setSearchParams(params, { replace: true });
    }, 400);
  };

  const handleSortChange = useCallback((sort: string) => {
    setSortBy(sort);
    const params = new URLSearchParams(searchParams);
    params.set('sort', sort);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);
  // Reset filters when switching asset types
  useEffect(() => {
    setMarketMode('all');
    setCurrentPage(1);
    // Only reset contentReady if we haven't ever loaded (preserves UI during asset switches after first paint)
    if (!hasEverLoadedRef.current) setContentReady(false);
    setSelectedMapListingId(null);
    setSelectedListingId(null);
    // Clear all listing selection states to prevent stale data across asset types
    setSelectedListing(null);
    setSelectedPartsListing(null);
    setSelectedJobListing(null);
    setSelectedServiceListing(null);
    setPartsDetailOpen(false);
    setJobDetailOpen(false);
    setServiceDetailOpen(false);
    // Clear listing params from URL to prevent stale ID leaking across asset types
    const params = new URLSearchParams(window.location.search);
    params.delete('listing');
    params.delete('engine');
    params.delete('parts');
    params.delete('job');
    params.delete('service');
    setSearchParams(params, { replace: true });
    setFilters({
      priceRange: null,
      seatingRange: null,
      tsnRange: null,
      manufacturers: [],
      models: [],
      listingType: 'all',
      condition: 'all',
      yearRange: null,
      location: '',
      jobCategories: [],
      employmentTypes: [],
      serviceCategories: [],
      aogSupportOnly: false,
    });
    setSellListingType(null);
    setBuyRequestType(null);
    setPartsListingType(null);
  }, [assetType]);

  // Reset map interaction state when switching asset types
  // Prevents leftover state from previous asset type causing premature boundingBox filtering
  useEffect(() => {
    boundsUpdateCountRef.current = 0;
    setUserHasInteractedWithMap(false);
    setMapBounds(null);
  }, [assetType, viewMode]); // Also reset on view mode change for stability

  // Set default view to map if not specified
  useEffect(() => {
    if (!searchParams.get('view')) {
      const params = new URLSearchParams(searchParams);
      params.set('view', 'map');
      setSearchParams(params, { replace: true });
    }
  }, []);

  // Event listeners for buy request response modals across all asset types
  useEffect(() => {
    const handleHelicopterResponse = (e: CustomEvent) => {
      setSelectedRequestForListing(e.detail.request);
      setResponseAssetType('helicopter');
    };
    const handleEngineResponse = (e: CustomEvent) => {
      setSelectedRequestForListing(e.detail.request);
      setResponseAssetType('engine');
    };
    const handlePartsResponse = (e: CustomEvent) => {
      setSelectedRequestForListing(e.detail.request);
      setResponseAssetType('parts');
    };
    const handleJobsResponse = (e: CustomEvent) => {
      setSelectedRequestForListing(e.detail.request);
      setResponseAssetType('jobs');
    };
    const handleServicesResponse = (e: CustomEvent) => {
      setSelectedRequestForListing(e.detail.request);
      setResponseAssetType('services');
    };

    window.addEventListener('open-buy-request-response', handleHelicopterResponse as EventListener);
    window.addEventListener('open-engine-buy-request-response', handleEngineResponse as EventListener);
    window.addEventListener('open-parts-buy-request-response', handlePartsResponse as EventListener);
    window.addEventListener('open-job-offer-response', handleJobsResponse as EventListener);
    window.addEventListener('open-service-offer-response', handleServicesResponse as EventListener);

    return () => {
      window.removeEventListener('open-buy-request-response', handleHelicopterResponse as EventListener);
      window.removeEventListener('open-engine-buy-request-response', handleEngineResponse as EventListener);
      window.removeEventListener('open-parts-buy-request-response', handlePartsResponse as EventListener);
      window.removeEventListener('open-job-offer-response', handleJobsResponse as EventListener);
      window.removeEventListener('open-service-offer-response', handleServicesResponse as EventListener);
    };
  }, []);

  // Sync modal state from URL - handles browser back/forward navigation
  useEffect(() => {
    // Skip sync while user is actively typing in search — prevents reopening stale listings
    if (isTypingRef.current) return;

    // Get listing ID from URL - check 'listing' param first, then asset-specific params
    const listingParam = searchParams.get('listing');
    const engineParam = searchParams.get('engine');
    const partsParam = searchParams.get('parts');
    const jobParam = searchParams.get('job');
    const serviceParam = searchParams.get('service');
    
    const urlSelectedId = listingParam || engineParam || partsParam || jobParam || serviceParam;
    
    if (urlSelectedId && effectiveListings.length > 0) {
      // If this was a local click, handleListingClick already set all state — skip redundant writes
      if (localModalOpenRef.current) {
        localModalOpenRef.current = false;
        return;
      }
      // External navigation (deep-link, browser back/forward) — do full sync
      const listing = effectiveListings.find(l => l.id === urlSelectedId);
      if (listing) {
        if (selectedMapListingId !== urlSelectedId) {
          setSelectedMapListingId(urlSelectedId);
        }
        if (selectedListingId !== urlSelectedId) {
          setSelectedListingId(urlSelectedId);
        }
        if (assetType === 'jobs') {
          if (!selectedJobListing || selectedJobListing.id !== urlSelectedId) {
            setSelectedJobListing(listing);
            setJobDetailOpen(true);
          }
        } else if (assetType === 'services') {
          if (!selectedServiceListing || selectedServiceListing.id !== urlSelectedId) {
            setSelectedServiceListing(listing);
            setServiceDetailOpen(true);
          }
        } else if (assetType === 'parts') {
          if (!selectedPartsListing || selectedPartsListing.id !== urlSelectedId) {
            setSelectedPartsListing(listing);
            setPartsDetailOpen(true);
          }
        } else if (!selectedListing || selectedListing.id !== urlSelectedId) {
          setSelectedListing(listing);
        }
        // Scroll to listing card on deep-link hydration
        setTimeout(() => {
          const cardElement = document.getElementById(`listing-card-${urlSelectedId}`);
          if (cardElement) {
            cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      }
    } else if (!urlSelectedId) {
      // URL has no listing ID - close any open modals (browser back was pressed)
      if (selectedListing) {
        setSelectedListing(null);
        setSelectedListingId(null);
      }
      if (selectedPartsListing) {
        setSelectedPartsListing(null);
        setPartsDetailOpen(false);
      }
      if (selectedJobListing) {
        setSelectedJobListing(null);
        setJobDetailOpen(false);
      }
      if (selectedServiceListing) {
        setSelectedServiceListing(null);
        setServiceDetailOpen(false);
      }
    }
  }, [searchParams.get('listing'), searchParams.get('engine'), searchParams.get('parts'), searchParams.get('job'), searchParams.get('service'), effectiveListings.length, assetType]);




  // URL sync effect — only handles filter/page/asset/mode params, NOT listing selection params.
  // Listing selection params (listing/engine/parts/job/service) are handled exclusively by handleListingClick
  // and modal close handlers to prevent duplicate URL writes that cause flicker.
  useEffect(() => {
    // Read from actual URL (not React Router state) to preserve modal params set via replaceState
    const params = new URLSearchParams(window.location.search);
    // Only write page param if it differs from current URL to avoid unnecessary updates
    const currentUrlPage = params.get('page');
    if (currentUrlPage !== currentPage.toString()) {
      params.set('page', currentPage.toString());
    }
    params.set('asset', assetType);
    
    if (assetType === 'helicopter') {
      if (sellListingType) {
        params.set('mode', 'sell');
        params.set('type', sellListingType);
      } else if (buyRequestType) {
        params.set('mode', 'buy');
        params.set('type', buyRequestType);
      } else {
        params.delete('mode');
        params.delete('type');
      }
      // Do NOT touch 'listing' param here — handled by handleListingClick / modal close
      params.delete('parts_type');
    } else if (assetType === 'parts') {
      params.delete('mode');
      params.delete('type');
      params.delete('engine');
      if (partsListingType) {
        params.set('parts_type', partsListingType);
      } else {
        params.delete('parts_type');
      }
      // Do NOT touch 'parts' param here
    } else if (assetType === 'jobs') {
      params.delete('mode');
      params.delete('type');
      params.delete('parts_type');
      params.delete('engine');
      params.delete('parts');
      params.delete('listing');
      params.delete('service');
      // Do NOT touch 'job' param here
    } else if (assetType === 'services') {
      params.delete('mode');
      params.delete('type');
      params.delete('parts_type');
      params.delete('engine');
      params.delete('parts');
      params.delete('listing');
      params.delete('job');
      // Do NOT touch 'service' param here
    } else {
      // Engine marketplace
      params.delete('mode');
      params.delete('type');
      params.delete('parts_type');
      params.delete('listing');
      params.delete('parts');
      // Do NOT touch 'engine' param here
    }
    
    // Only update URL if params actually changed — prevents redundant writes that cause flicker
    const currentSearch = new URLSearchParams(window.location.search).toString();
    if (params.toString() !== currentSearch) {
      setSearchParams(params, { replace: true });
    }
  }, [sellListingType, buyRequestType, currentPage, assetType, partsListingType]);

  // filteredListings removed — use effectiveListings directly (same reference as paginatedQuery.allListings)

  // Gather listing IDs with hidden locations for grant checking
  const hiddenLocationListingIds = useMemo(() => {
    return effectiveListings
      .filter(listing => {
        const visibilitySettings = listing.visibility_settings as any;
        return visibilitySettings && !visibilitySettings.use_global_defaults && visibilitySettings.hide_location;
      })
      .map(listing => listing.id);
  }, [effectiveListings]);

  // Fetch location grants for hidden listings (only if user is logged in)
  const { grantsMap: locationGrantsMap } = useLocationGrantsMap(hiddenLocationListingIds);

  // Helper to detect if a listing is a request (for rendering dividers)
  const checkIsRequestListing = useCallback((listing: any): boolean => {
    if (assetType === 'helicopter') {
      return listing.is_buy_request === true;
    }
    if (assetType === 'jobs') {
      return listing.listing_type === 'job_profile' || listing.listing_type === 'job_seeking';
    }
    if (assetType === 'services') {
      return listing.listing_type === 'service_request' || listing.listing_type === 'rfq_request';
    }
    const listingType = listing.listing_type || '';
    return listingType.includes('buy') || listingType.includes('request');
  }, [assetType]);

  // Group paginated listings by listing_type for category dividers
  // Shared helper to group listings by type and sort groups
  const getGroupedByType = useCallback((listings: any[], page: number, perPage: number) => {
    const startIndex = (page - 1) * perPage;
    const paginated = listings.slice(startIndex, startIndex + perPage);
    const groups: Record<string, any[]> = {};
    paginated.forEach(listing => {
      let listingType: string;
      if (assetType === 'helicopter') {
        const baseType = listing.listing_type || 'sale';
        listingType = listing.is_buy_request ? `buy_${baseType}` : baseType;
      } else {
        listingType = listing.listing_type || 'unknown';
      }
      if (!groups[listingType]) groups[listingType] = [];
      groups[listingType].push(listing);
    });
    return Object.entries(groups).sort((a, b) =>
      getListingTypeSortOrder(a[0], assetType) - getListingTypeSortOrder(b[0], assetType)
    );
  }, [assetType]);

  const groupedListings = useMemo(() => {
    return getGroupedByType(effectiveListings, currentPage, itemsPerPage);
  }, [effectiveListings, currentPage, itemsPerPage, getGroupedByType]);

  const totalPages = Math.ceil(paginatedQuery.totalCount / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sellListingType, buyRequestType, debouncedSearchQuery, sortBy]);

  // Scroll to top when page changes (grid/list views)
  useEffect(() => {
    if (viewMode !== 'map') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage, viewMode]);

  // Auto-fetch next pages when user navigates beyond loaded data
  useEffect(() => {
    const neededItems = currentPage * itemsPerPage;
    if (neededItems > effectiveListings.length && paginatedQuery.hasNextPage && !paginatedQuery.isFetchingNextPage) {
      paginatedQuery.fetchNextPage();
    }
  }, [currentPage, itemsPerPage, effectiveListings.length, paginatedQuery.hasNextPage, paginatedQuery.isFetchingNextPage, paginatedQuery.fetchNextPage]);

  // Clear stale map selection when leaving map view
  useEffect(() => {
    if (viewMode !== 'map') {
      setSelectedMapListingId(null);
    }
  }, [viewMode]);

  // Shared hero image extraction helper
  const getHeroImage = useCallback((listing: any): string | null => {
    if (assetType === 'helicopter') return extractListingImages(listing)[0] || null;
    if (assetType === 'engine') return listing.heroImage || null;
    if (assetType === 'jobs') return listing.hero_image || listing.company_logo || null;
    if (assetType === 'services') return listing.hero_image || listing.company_logo || null;
    return listing.hero_image || listing.photos?.[0] || null;
  }, [assetType]);

  // Shared MobileListView prop builder to avoid duplicating ~200 lines across 3 locations
  const buildMobileListingProps = useCallback((listing: any) => {
    const lt = listing.listing_type || listing.listingType || '';
    const isBuy = listing.is_buy_request || lt.includes('buy') || lt.includes('request');
    const price = isBuy
      ? (listing.budget_max || listing.max_price || listing.price_usd || listing.price)
      : (listing.price_usd || listing.price);

    return {
      id: listing.id,
      title: listing.title || listing.headline,
      location: listing.location || listing.city,
      price,
      listingType: lt,
      heroImage: getHeroImage(listing),
      year: listing.year_manufactured || listing.year,
      flightHours: listing.total_flight_hours || listing.airframe_hours || listing.total_tsn || listing.tsn,
      assetType,
      isBuyRequest: isBuy,
      sellerId: listing.user_id,
      visibilitySettings: listing.visibility_settings,
      rentalRate: listing.rental_rate || listing.rentalRate,
      rentalPeriod: listing.rental_period || listing.rentalPeriod,
      maxBudget: listing.budget_max || listing.max_budget || listing.max_rate,
      ...(assetType === 'jobs' && {
        jobCategory: listing.job_category,
        employmentType: listing.employment_type,
        salaryMin: listing.salary_min,
        salaryMax: listing.salary_max,
        salaryType: listing.salary_type,
        companyName: listing.company_name,
        companyLogo: listing.company_logo,
        typeRatings: listing.type_ratings,
      }),
      ...(assetType === 'services' && {
        serviceCategory: listing.service_category,
        shiftCoverage: listing.shift_coverage,
        aogSupport: listing.aog_support,
        responseTime: listing.response_time,
        turnaroundTime: listing.turnaround_time,
        companyName: listing.company_name,
        companyLogo: listing.company_logo,
        pricingModel: listing.pricing_model,
        baseRate: listing.base_rate,
        rateUnit: listing.rate_unit,
      }),
      ...(assetType === 'parts' && {
        partsData: {
          itemType: listing.item_type,
          specificityType: listing.specificity_type,
          rentalRate: listing.rental_rate,
          rentalPeriod: listing.rental_period,
        },
      }),
    };
  }, [assetType, getHeroImage]);

  // Shared MobileMapPreviewCard prop builder to avoid duplicating ~35 lines
  const buildPreviewCardProps = useCallback((listing: any) => {
    const lt = listing.listing_type || listing.listingType || '';
    const isBuy = listing.is_buy_request || lt.includes('buy') || lt.includes('request');
    return {
      id: listing.id,
      title: listing.title || listing.headline,
      location: listing.location || listing.city,
      price: isBuy ? (listing.budget_max || listing.max_price || listing.price_usd || listing.price) : (listing.price_usd || listing.price),
      listingType: lt,
      heroImage: getHeroImage(listing),
      manufacturer: listing.manufacturer,
      model: listing.model,
      assetType,
      companyLogo: listing.company_logo,
      companyName: listing.company_name,
      serviceCategory: listing.service_category,
      sellerId: listing.user_id,
      visibilitySettings: listing.visibility_settings,
      ...(assetType === 'jobs' && { salaryMin: listing.salary_min, salaryMax: listing.salary_max, salaryType: listing.salary_type }),
      ...(assetType === 'services' && { pricingModel: listing.pricing_model, baseRate: listing.base_rate, rateUnit: listing.rate_unit }),
      ...(assetType === 'parts' && { rentalRate: listing.rental_rate, rentalPeriod: listing.rental_period }),
    };
  }, [assetType, getHeroImage]);

  const manufacturerCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    effectiveListings.forEach((listing) => {
      const mfg = assetType === 'engine'
        ? (listing as any).manufacturer
        : listing.helicopters?.manufacturer;
      if (mfg) {
        counts[mfg] = (counts[mfg] || 0) + 1;
      }
    });
    return counts;
  }, [effectiveListings, assetType]);

  const activeFiltersCount = React.useMemo(() => {
    let count = 0;
    if (filters.condition !== 'all') count++;
    if (filters.location) count++;
    if (filters.priceRange !== null) count++;
    if (filters.manufacturers.length > 0) count++;
    if (filters.models.length > 0) count++;

    // Helicopter-specific
    if (assetType === 'helicopter') {
      if (filters.seatingRange !== null) count++;
      if (filters.yearRange !== null) count++;
    }

    // Engine-specific
    if (assetType === 'engine') {
      if (filters.tsnRange !== null) count++;
    }

    // Jobs-specific
    if (assetType === 'jobs') {
      if (filters.jobCategories?.length > 0) count++;
      if (filters.employmentTypes?.length > 0) count++;
    }

    // Services-specific
    if (assetType === 'services') {
      if (filters.serviceCategories?.length > 0) count++;
      if (filters.aogSupportOnly) count++;
    }

    return count;
  }, [filters, assetType]);

  const listingsWithCoordinates: MapMarker[] = useMemo(() => {
    return effectiveListings
      .filter(listing => {
        // Must have coordinates
        if (listing.latitude == null || listing.longitude == null) return false;
        
        // Check visibility settings for hidden location
        const visibilitySettings = listing.visibility_settings as any;
        if (visibilitySettings && !visibilitySettings.use_global_defaults && visibilitySettings.hide_location) {
          // Check if user has been granted location access for this listing
          const hasLocationGrant = locationGrantsMap[listing.id];
          if (!hasLocationGrant) return false;
        }
        
        return true;
      })
      .map(listing => {
        if (assetType === 'engine') {
          const engineListing = listing as any;
          return {
            id: engineListing.id,
            position: { lat: engineListing.latitude, lng: engineListing.longitude },
            title: engineListing.title,
            price: engineListing.price_usd,
            manufacturer: engineListing.manufacturer || 'Unknown',
            model: engineListing.model || 'Unknown',
            listingType: engineListing.listing_type,
            heroImage: engineListing.heroImage || null,
            iconUrl: null
          };
        }
        
        if (assetType === 'parts') {
          const partsListing = listing as any;
          return {
            id: partsListing.id,
            position: { lat: partsListing.latitude, lng: partsListing.longitude },
            title: partsListing.title,
            price: partsListing.price,
            manufacturer: partsListing.manufacturer || '',
            model: partsListing.part_number || '',
            listingType: partsListing.listing_type,
            heroImage: partsListing.hero_image || null,
            iconUrl: null
          };
        }
        
        if (assetType === 'jobs') {
          const jobListing = listing as any;
          return {
            id: jobListing.id,
            position: { lat: jobListing.latitude, lng: jobListing.longitude },
            title: jobListing.title,
            price: jobListing.salary_max || jobListing.salary_min,
            manufacturer: jobListing.company_name || 'Company',
            model: jobListing.job_category?.replace(/_/g, ' ') || 'Job',
            listingType: jobListing.listing_type,
            heroImage: jobListing.hero_image || jobListing.company_logo || null,
            iconUrl: null,
            jobCategory: jobListing.job_category
          };
        }
        
        if (assetType === 'services') {
          const serviceListing = listing as any;
          const categoryLabel = getServiceCategoryLabel(serviceListing.service_category);
          
          const isProvider = serviceListing.listing_type === 'service_offer' || serviceListing.listing_type === 'capability_listing';
          const heroImage = isProvider
            ? (serviceListing.company_logo || serviceListing.hero_image || null)
            : (serviceListing.hero_image || serviceListing.company_logo || null);
          
          return {
            id: serviceListing.id,
            position: { lat: serviceListing.latitude, lng: serviceListing.longitude },
            title: serviceListing.title,
            price: serviceListing.base_rate,
            manufacturer: serviceListing.company_name || 'Company',
            model: categoryLabel,
            listingType: serviceListing.listing_type,
            heroImage,
            iconUrl: null,
            notes: categoryLabel,
            serviceCategory: serviceListing.service_category
          };
        }
        
        return {
          id: listing.id,
          position: { lat: listing.latitude!, lng: listing.longitude! },
          title: listing.title,
          price: listing.price_usd,
          manufacturer: listing.helicopters?.manufacturer || 'Unknown',
          model: listing.helicopters?.model || 'Unknown',
          radius: listing.search_radius,
          listingType: listing.listing_type,
          heroImage: extractListingImages(listing)[0] || null,
          iconUrl: listing.helicopters?.icon_url || null,
          configurationName: listing.configuration_name
        };
      });
  }, [effectiveListings, assetType, locationGrantsMap]);

  // Refs for stable callback — avoids stale closures in memo'd cards
  const selectedListingIdRef = useRef(selectedListingId);
  useEffect(() => { selectedListingIdRef.current = selectedListingId; }, [selectedListingId]);

  const handleListingClick = useCallback((listing: any) => {
    // Mark as local open so the URL→modal sync effect doesn't scroll
    localModalOpenRef.current = true;

    // Read current params from actual URL (not React Router state) to preserve all params
    // Use replaceState instead of setSearchParams to avoid triggering React Router re-renders
    const params = new URLSearchParams(window.location.search);
    
    // Handle jobs separately - use dedicated state and 'job' URL param
    if (assetType === 'jobs') {
      setSelectedJobListing(listing);
      setJobDetailOpen(true);
      setSelectedListingId(listing.id);
      params.set('job', listing.id);
      window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
      return;
    }
    
    // Handle services separately - use dedicated state and 'service' URL param
    if (assetType === 'services') {
      setSelectedServiceListing(listing);
      setServiceDetailOpen(true);
      setSelectedListingId(listing.id);
      params.set('service', listing.id);
      window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
      return;
    }
    
    // Handle parts separately - use dedicated state
    if (assetType === 'parts') {
      setSelectedPartsListing(listing);
      setPartsDetailOpen(true);
      setSelectedListingId(listing.id);
      params.set('parts', listing.id);
      window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
      return;
    }
    
    setSelectedListing(listing);
    setSelectedListingId(listing.id);
    
    if (assetType === 'helicopter') {
      params.set('listing', listing.id);
    } else if (assetType === 'engine') {
      params.set('engine', listing.id);
    }
    window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
  }, [assetType]);

  const handleMapListingSelect = useCallback((listingId: string) => {
    // Skip if already selected to avoid unnecessary re-renders
    setSelectedMapListingId(prev => {
      if (prev === listingId) return prev;
      return listingId;
    });
    // Just highlight and scroll to the card - don't open the modal
    setTimeout(() => {
      const cardElement = document.getElementById(`listing-card-${listingId}`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  // Map ready callback for synchronized loading
  const handleMapReady = useCallback(() => {
    setContentReady(true);
    hasEverLoadedRef.current = true;
  }, []);

  // Handle map bounds change for viewport-based filtering (debounced)
  // Skip first 3 updates (initial load + fitBounds + immediate adjustment) to prevent infinite loop
  const handleBoundsChanged = useCallback((bounds: { north: number; south: number; east: number; west: number }) => {
    boundsUpdateCountRef.current += 1;
    
    // Skip first 3 bounds updates to account for:
    // 1. Initial map render
    // 2. fitBounds auto-center
    // 3. Possible immediate bounds adjustment
    // Only start filtering after user has intentionally interacted with the map
    if (boundsUpdateCountRef.current <= 3) {
      console.debug(`[Map] Skipping bounds update ${boundsUpdateCountRef.current}/3`);
      return;
    }
    
    // Mark that user has interacted with map
    if (!userHasInteractedWithMap) {
      setUserHasInteractedWithMap(true);
    }
    
    // Only update if significantly different to avoid excessive refetches
    setMapBounds(prev => {
      if (!prev) return bounds;
      const threshold = 0.01; // ~1km
      if (
        Math.abs(prev.north - bounds.north) > threshold ||
        Math.abs(prev.south - bounds.south) > threshold ||
        Math.abs(prev.east - bounds.east) > threshold ||
        Math.abs(prev.west - bounds.west) > threshold
      ) {
        return bounds;
      }
      return prev;
    });
  }, [userHasInteractedWithMap]);

  // Fallback timeout - show content after 2s even if map isn't ready
  // Once content is shown, never hide again (prevents flicker on refetches/modal opens)
  useEffect(() => {
    if (hasEverLoadedRef.current) return; // Already shown, never hide again
    if (!effectiveLoading && viewMode === 'map') {
      const timer = setTimeout(() => {
        setContentReady(true);
        hasEverLoadedRef.current = true;
      }, 2000);
      return () => clearTimeout(timer);
    }
    if (!effectiveLoading && viewMode !== 'map') {
      setContentReady(true);
      hasEverLoadedRef.current = true;
    }
  }, [effectiveLoading, viewMode]);

  // Sort listings to put selected one first
  const sortedListingsForDisplay = useMemo(() => {
    if (!selectedMapListingId) return effectiveListings;
    
    const sel = effectiveListings.find(l => l.id === selectedMapListingId);
    if (!sel) return effectiveListings;
    
    return [
      sel,
      ...effectiveListings.filter(l => l.id !== selectedMapListingId)
    ];
  }, [effectiveListings, selectedMapListingId]);

  const handleClearAllFilters = () => {
    setSellListingType(null);
    setBuyRequestType(null);
    setPartsListingType(null);
    onManufacturersChange([]);
    setSearchInput('');
    setDebouncedSearchQuery('');
    setMarketMode('all');
    setSortBy('date-desc');
    setCurrentPage(1);
    // Remove search, market, and other filter params from URL
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    params.delete('market');
    params.delete('parts_type');
    params.delete('sort');
    params.delete('mode');
    params.delete('type');
    params.set('page', '1');
    setSearchParams(params, { replace: true });
    setFilters({
      priceRange: null,
      seatingRange: null,
      tsnRange: null,
      manufacturers: [],
      models: [],
      listingType: 'all',
      condition: 'all',
      yearRange: null,
      location: '',
      jobCategories: [],
      serviceCategories: [],
      employmentTypes: [],
      aogSupportOnly: false,
    });
  };

  const onManufacturersChange = (manufacturers: string[]) => {
    setFilters({ ...filters, manufacturers, models: [] });
  };

  // Only show full-page skeleton on initial load (before any data has ever rendered).
  // After first successful paint, keep the current UI visible during refetches to prevent flicker.
  if (effectiveLoading && !hasEverLoadedRef.current) {
    return (
      <div className="min-h-screen bg-background pb-bottom-nav lg:pb-0">
        {!isMobile && <Navigation />}
        <div className="max-w-[2200px] mx-auto px-6 py-8">
          <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {[...Array(8)].map((_, i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="h-60 w-full rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const seo = getMarketplaceSEO(assetType, paginatedQuery.totalCount);

  return (
    <div className={cn("min-h-screen bg-background lg:pb-0", viewMode === 'map' ? 'pb-0 overflow-hidden' : 'pb-bottom-nav')}>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={`https://chopperswap.com/marketplace?asset=${assetType}`} />
      </Helmet>
      
      {/* Mobile header is consolidated into the fixed search/filter bar below */}
      
      {/* Navigation - Tablet + Desktop (hide on phone) */}
      {!isMobile && (
        <Navigation />
      )}
      
      {/* Desktop Filter Bar - Tablet + Desktop (hide on phone) */}
      <div className={cn(isMobile ? 'hidden' : 'block', 'sticky z-40 bg-background border-b shadow-sm transition-all duration-300', isTablet ? 'top-0' : 'top-[var(--nav-height-desktop)]')}>
        <div className="max-w-[2200px] mx-auto px-6 py-4">
          {filtersCollapsed ? (
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setFiltersCollapsed(false)}
                className="gap-2"
                data-tutorial-id="marketplace-show-filters"
              >
                <SlidersHorizontal className="h-4 w-4" />
                Show Filters
                {activeFiltersCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>
              <div className="flex items-center gap-3">
                {/* Asset Type Toggle */}
                <div data-tutorial-id="marketplace-asset-selector" className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
                  {renderAssetToggle('collapsed')}
                </div>
                
                {/* Market Mode Toggle - Sale vs Buy */}
                <div data-tutorial-id="marketplace-mode-toggle" className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
                  {renderMarketModeToggle('collapsed')}
                </div>
                
                {/* View Mode Buttons */}
                <div className="flex items-center gap-1">
                  {renderViewModeButtons()}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-3">
                {/* Asset Type Toggle - Premium Glass Container */}
                <div 
                  data-tutorial-id="marketplace-asset-selector" 
                  className={cn(
                    "flex items-center gap-1 p-1.5 rounded-2xl",
                    "bg-gradient-to-r from-slate-100 via-white to-slate-100",
                    "dark:from-slate-800/80 dark:via-slate-900/80 dark:to-slate-800/80",
                    "border border-slate-200/80 dark:border-slate-700/50",
                    "shadow-lg shadow-slate-200/50 dark:shadow-black/20",
                    "backdrop-blur-sm"
                  )}
                >
                  {renderAssetToggle('desktop')}
                </div>
                
                {/* Market Mode Toggle - Intent-Based Visual Theming */}
                <div 
                  data-tutorial-id="marketplace-mode-toggle" 
                  className={cn(
                    "flex items-center gap-0.5 p-1 rounded-xl",
                    "bg-white dark:bg-slate-900",
                    "border border-slate-200/60 dark:border-slate-700/40",
                    "shadow-md"
                  )}
                >
                  {renderMarketModeToggle('desktop')}
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFiltersCollapsed(true)}
                  className="gap-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Hide Filters
                </Button>
              </div>
              
              {/* Subtle divider before filter hierarchy */}
              <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-300 dark:via-slate-600 to-transparent mb-3" />
              
              <FilterHierarchy
                assetType={assetType}
                searchQuery={searchInput}
                onSearchChange={handleSearchChange}
                sortBy={sortBy}
                onSortChange={handleSortChange}
                sellListingType={sellListingType}
                buyRequestType={buyRequestType}
                onSellTypeChange={setSellListingType}
                onBuyTypeChange={setBuyRequestType}
                partsListingType={partsListingType}
                onPartsTypeChange={setPartsListingType}
                manufacturers={effectiveManufacturers}
                selectedManufacturers={filters.manufacturers}
                manufacturerCounts={manufacturerCounts}
                onManufacturersChange={(manufacturers) => setFilters({ ...filters, manufacturers, models: [] })}
                filters={filters}
                onFiltersChange={setFilters}
                showAdvancedFilters={showFilters}
                onToggleAdvancedFilters={() => setShowFilters(!showFilters)}
                activeFiltersCount={activeFiltersCount}
                viewMode={viewMode}
                onViewModeChange={(mode) => {
                  setViewMode(mode);
                  const params = new URLSearchParams(searchParams);
                  params.set('view', mode);
                  setSearchParams(params, { replace: true });
                }}
                markerDisplayMode={markerDisplayMode}
                onMarkerDisplayModeChange={setMarkerDisplayMode}
                marketMode={marketMode}
                onMarketModeChange={(mode) => {
                  setMarketMode(mode);
                  setCurrentPage(1);
                  const params = new URLSearchParams(searchParams);
                  if (mode === 'all') params.delete('market');
                  else params.set('market', mode);
                  setSearchParams(params, { replace: true });
                }}
              />
            </>
          )}
        </div>
      </div>

      {/* Mobile Search + Filter Bar - Enhanced with Glass Morphism */}
      {isMobile && (
        <div className={cn(
          "fixed top-0 left-0 right-0 z-50",
          "bg-background/85 backdrop-blur-xl",
          "border-b-0",
          "shadow-lg shadow-black/5",
          "pt-[env(safe-area-inset-top,0px)]"
        )}>
          {/* Compact title row */}
          <div className="flex items-center justify-between px-3 pt-2 pb-1">
            <span className="text-sm font-semibold text-foreground">Marketplace</span>
            <span className="text-[11px] text-muted-foreground">
              {paginatedQuery.totalCount} {assetType === 'helicopter' ? 'Helicopters' : assetType === 'engine' ? 'Engines' : assetType === 'parts' ? 'Parts' : assetType === 'jobs' ? 'Jobs' : 'Services'}
            </span>
          </div>
          {/* Row 1: Asset Type + Market Mode */}
          <div className="flex items-center justify-between px-3 pb-2 gap-2">
            {/* Asset Type Toggle - Enhanced Pills */}
            <div className={cn(
              "flex gap-0.5 p-1 rounded-xl",
              "bg-muted/50 backdrop-blur-sm",
              "border border-border/20"
            )}>
              {renderAssetToggle('mobile')}
            </div>
            
            {/* Market Mode Toggle - Enhanced with context-aware labels */}
            <div className={cn(
              "flex gap-0.5 p-1 rounded-xl",
              "bg-muted/50 backdrop-blur-sm",
              "border border-border/20"
            )}>
              {renderMarketModeToggle('mobile')}
            </div>
          </div>
          
          {/* Row 2: Search (left) + Filters (right) + View Toggle */}
          <div className="flex items-center gap-2 px-3 pb-3">
            {/* Search Bar - flex-1 */}
            <div className={cn(
              "relative rounded-xl overflow-hidden flex-1 min-w-0",
              "bg-muted/30 backdrop-blur-sm",
              "border border-border/20",
              "focus-within:border-primary/40 focus-within:shadow-md focus-within:shadow-primary/10",
              "transition-all duration-300"
            )}>
              <MarketplaceSearchInput
                assetType={assetType}
                value={searchInput}
                onChange={handleSearchChange}
                onClearInput={() => setSearchInput('')}
                onSubmit={(val) => {
                  if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                  isTypingRef.current = false;
                  setDebouncedSearchQuery(val);
                  const params = new URLSearchParams(window.location.search);
                  if (val) params.set('search', val);
                  else params.delete('search');
                  params.delete('listing');
                  params.delete('engine');
                  params.delete('parts');
                  params.delete('job');
                  params.delete('service');
                  setSearchParams(params, { replace: true });
                }}
                className="w-full border-0 bg-transparent focus-visible:ring-0"
              />
            </div>

            {/* Filter Button (icon-only on mobile) */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMobileFiltersOpen(true)}
              aria-label="Filters"
              className={cn(
                "relative flex items-center justify-center h-9 w-9 p-0 rounded-xl shrink-0",
                "bg-background/50 backdrop-blur-sm",
                "border-border/30 hover:border-primary/30",
                "transition-all duration-300",
                activeFiltersCount > 0 && "border-primary/50 bg-primary/5"
              )}
            >
              <SlidersHorizontal className="h-4 w-4" />
              {activeFiltersCount > 0 && (
                <Badge
                  variant="default"
                  className={cn(
                    "absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[9px]",
                    "bg-primary text-primary-foreground",
                    "animate-in zoom-in-50 duration-200"
                  )}
                >
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>

            {/* View Toggle */}
            <div className={cn(
              "flex gap-0.5 p-1 rounded-xl shrink-0",
              "bg-muted/50 backdrop-blur-sm",
              "border border-border/20"
            )}>
              {renderViewModeButtons()}
            </div>
          </div>

          {/* Row 4: Category Quick Filters for Jobs/Services - Mobile Only */}
          {(assetType === 'jobs' || assetType === 'services') && (
            <div className="px-3 pb-2 overflow-x-auto scrollbar-hide">
              <div className="flex gap-1.5 min-w-max">
                <span className="text-[9px] font-medium text-muted-foreground/70 uppercase tracking-wider mr-1 self-center">
                  {assetType === 'services' ? 'Category' : 'Role'}
                </span>
                {(assetType === 'services'
                  ? (Object.entries(SERVICE_CATEGORY_CONFIG) as [ServiceCategory, { label: string; shortLabel: string }][]).map(([k, v]) => ({ value: k, shortLabel: v.shortLabel }))
                  : JOB_CATEGORY_OPTIONS
                ).map(cat => {
                  const filterKey = assetType === 'services' ? 'serviceCategories' : 'jobCategories';
                  const currentValues = (filters[filterKey] || []) as string[];
                  const isSelected = currentValues.includes(cat.value);
                  return (
                    <button
                      key={cat.value}
                      onClick={() => {
                        const updated = isSelected
                          ? currentValues.filter((c: string) => c !== cat.value)
                          : [...currentValues, cat.value];
                        setFilters({ ...filters, [filterKey]: updated });
                      }}
                      className={cn(
                        "px-2 py-1 rounded-full text-[10px] font-medium transition-all border flex-shrink-0",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        isSelected
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-card text-muted-foreground border-border/50 hover:bg-muted/50"
                      )}
                    >
                      {cat.shortLabel}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mobile Filter Drawer */}
      <MobileFilterDrawer
        assetType={assetType}
        open={mobileFiltersOpen}
        onOpenChange={setMobileFiltersOpen}
        searchQuery={searchInput}
        onSearchChange={handleSearchChange}
        sortBy={sortBy}
        onSortChange={handleSortChange}
        sellListingType={sellListingType}
        buyRequestType={buyRequestType}
        onSellTypeChange={setSellListingType}
        onBuyTypeChange={setBuyRequestType}
        partsListingType={partsListingType}
        onPartsTypeChange={setPartsListingType}
        manufacturers={effectiveManufacturers}
        selectedManufacturers={filters.manufacturers}
        manufacturerCounts={manufacturerCounts}
        onManufacturersChange={onManufacturersChange}
        filters={filters}
        onFiltersChange={setFilters}
        onClearFilters={handleClearAllFilters}
        marketMode={marketMode}
        onMarketModeChange={(mode) => {
          setMarketMode(mode);
          setCurrentPage(1);
          const params = new URLSearchParams(searchParams);
          if (mode === 'all') params.delete('market');
          else params.set('market', mode);
          setSearchParams(params, { replace: true });
        }}
      />
      
      {/* Spacer to prevent content overlap with fixed filter bar - Phone only */}
      {isMobile && <div className={cn("h-[112px]", (assetType === 'jobs' || assetType === 'services') && "h-[140px]")} style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}></div>}
      {/* Desktop/Tablet spacer - only when NOT in map view */}
      {!isMobile && viewMode !== 'map' && <div className="h-16"></div>}


      <MarketplaceViewport
        viewMode={viewMode}
        assetType={assetType}
        isMobile={isMobile}
        isTablet={isTablet}
        markerDisplayMode={markerDisplayMode}
        effectiveListings={effectiveListings}
        effectiveLoading={effectiveLoading}
        sortedListingsForDisplay={sortedListingsForDisplay}
        groupedListings={groupedListings}
        listingsWithCoordinates={listingsWithCoordinates}
        totalCount={paginatedQuery.totalCount}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        selectedMapListingId={selectedMapListingId}
        buyRequestType={buyRequestType}
        activeFiltersCount={activeFiltersCount}
        searchInput={searchInput}
        handleListingClick={handleListingClick}
        handleMapListingSelect={handleMapListingSelect}
        handleMapReady={handleMapReady}
        handleBoundsChanged={handleBoundsChanged}
        onClearFilters={handleClearAllFilters}
        setSelectedMapListingId={setSelectedMapListingId}
        getGroupedByType={getGroupedByType}
        buildMobileListingProps={buildMobileListingProps}
        buildPreviewCardProps={buildPreviewCardProps}
      />

      {selectedListing && assetType === 'helicopter' && (
        <ListingDetailModal
          listingId={selectedListing.id}
          trigger={<div />}
          open={!!selectedListing}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedListing(null);
              setSelectedListingId(null);
              // Remove listing from URL when closing — use replaceState to avoid re-render
              const params = new URLSearchParams(window.location.search);
              params.delete('listing');
              window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
            }
          }}
        />
      )}
      
      {selectedListing && assetType === 'engine' && (
        <EngineListingDetailModal
          listing={selectedListing}
          open={!!selectedListing}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedListing(null);
              setSelectedListingId(null);
              // Remove engine from URL when closing — use replaceState to avoid re-render
              const params = new URLSearchParams(window.location.search);
              params.delete('engine');
              window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
            }
          }}
        />
      )}

      {selectedPartsListing && assetType === 'parts' && (
        <PartsListingDetailModal
          listing={selectedPartsListing}
          open={partsDetailOpen}
          onOpenChange={(open) => {
            setPartsDetailOpen(open);
            if (!open) {
              setSelectedPartsListing(null);
              setSelectedListingId(null);
              // Remove parts from URL when closing — use replaceState to avoid re-render
              const params = new URLSearchParams(window.location.search);
              params.delete('parts');
              window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
            }
          }}
        />
      )}

      {selectedJobListing && assetType === 'jobs' && (
        <JobListingDetailModal
          listing={selectedJobListing}
          isOpen={jobDetailOpen}
          onClose={() => {
            setJobDetailOpen(false);
            setSelectedJobListing(null);
            setSelectedListingId(null);
            const params = new URLSearchParams(window.location.search);
            params.delete('job');
            window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
          }}
          onContact={() => {
            if (!user) {
              window.dispatchEvent(new CustomEvent('require-auth', {
                detail: { message: 'Sign in to contact this employer' }
              }));
              return;
            }
            if (selectedJobListing?.user_id && user.id !== selectedJobListing.user_id) {
              createOrGetConversation(selectedJobListing.id, selectedJobListing.user_id, selectedJobListing.title || 'Job Inquiry', 'jobs').then(convId => {
                if (convId) navigate(`/messages?conversation=${convId}`);
              });
            }
          }}
        />
      )}

      {selectedServiceListing && assetType === 'services' && (
        <ServiceDetailModal
          listing={selectedServiceListing}
          isOpen={serviceDetailOpen}
          onClose={() => {
            setServiceDetailOpen(false);
            setSelectedServiceListing(null);
            setSelectedListingId(null);
            const params = new URLSearchParams(window.location.search);
            params.delete('service');
            window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
          }}
          onContact={() => {
            if (!user) {
              window.dispatchEvent(new CustomEvent('require-auth', {
                detail: { message: 'Sign in to contact this provider' }
              }));
              return;
            }
            if (selectedServiceListing?.user_id && user.id !== selectedServiceListing.user_id) {
              createOrGetConversation(selectedServiceListing.id, selectedServiceListing.user_id, selectedServiceListing.title || 'Service Inquiry', 'services').then(convId => {
                if (convId) navigate(`/messages?conversation=${convId}`);
              });
            }
          }}
          onNavigate={() => {
            setServiceDetailOpen(false);
            setSelectedServiceListing(null);
            setSelectedListingId(null);
            const params = new URLSearchParams(window.location.search);
            params.delete('service');
            window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
          }}
        />
      )}

      {/* Unified Buy Request Response Modal for all asset types */}
      {selectedRequestForListing && (
        <UnifiedBuyRequestResponseModal
          isOpen={!!selectedRequestForListing}
          onClose={() => setSelectedRequestForListing(null)}
          request={selectedRequestForListing}
          assetType={responseAssetType}
          onContactFlow={() => {
            // Handle contact flow - could navigate to messages or open contact modal
            setSelectedRequestForListing(null);
          }}
        />
      )}
    </div>
  );
};

export default EnhancedMarketplace;
